import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  TrendingUp, TrendingDown, Users, MessageCircle, MessagesSquare, 
  Activity, ChevronDown, BarChart3, Calendar, Eye, EyeOff,
  Layers, Target, RefreshCw, Info, AlertCircle, Presentation, X,
  ThumbsUp, ThumbsDown, ArrowUpRight, ArrowDownRight, Minus, Trophy, Briefcase
} from 'lucide-react';
import './ComprehensiveAnalytics.css';
import OidcAuthService from '../services/oidcAuth';
import type { Agent } from '../types';

// Types
interface AgentData {
  id: string;
  name: string;
  color: string;
  totalMessages: number;
  totalUsers: number;
  totalSessions: number;
  dailyData: Array<{
    date: string;
    messages: number;
    users: number;
    sessions: number;
  }>;
}

interface TimeSeriesPoint {
  date: string;
  total: number;
  [agentId: string]: number | string;
}

interface TopicCategory {
  topic: string;
  count: number;
  percentage: number;
}

interface Keyword {
  word: string;
  count: number;
}

interface CommonTopics {
  categories: TopicCategory[];
  keywords: Keyword[];
  keywordsByAgent?: Record<string, Keyword[]>; // Keywords filtered by agent
}

interface SuggestionClickData {
  totalClicks: number;
  byAgent: Array<{
    agentId: string;
    clicks: number;
    uniqueUsers: number;
  }>;
  topSuggestions: Array<{
    text: string;
    count: number;
  }>;
}

interface FeedbackStats {
  positive: number;
  negative: number;
  total: number;
  approvalRate: number;  // Calculated: positive / total * 100
}

interface AnalyticsData {
  agents: AgentData[];
  timeSeries: TimeSeriesPoint[];
  totals: {
    messages: number;
    users: number;
    sessions: number;
    daysTracked: number;
    // New fields for suggestion tracking
    suggestionClicks?: number;
    typedMessages?: number;
    allInteractions?: number;
    // User Analytics Framework
    // Dimension 1: Lifecycle Status (acquisition)
    newUsers?: number;           // First-ever interaction in this period
    existingUsers?: number;      // First-ever interaction before this period
    // Dimension 2: Engagement (behavior in period)
    oneTimeUsers?: number;       // Exactly 1 interaction in period
    returningUsers?: number;     // ≥2 interactions in period
    multiDayUsers?: number;      // Users meeting active-days threshold
    activeDaysThreshold?: number; // Dynamic threshold based on time window
    // Derived metrics
    avgInteractionsPerUser?: number;
    // Cross-tabulation
    userSegments?: {
      newOneTime: number;
      newReturning: number;
      existingOneTime: number;
      existingReturning: number;
      pctNewReturned: number;    // % of new users who returned
    };
  };
  commonTopics: CommonTopics;
  suggestionClicks?: SuggestionClickData;
  feedback?: FeedbackStats;
  dateRange: {
    start: string | null;
    end: string | null;
  };
}

type TimePeriod = 'week' | 'month' | '3months' | '6months' | 'year' | 'all';

interface ComprehensiveAnalyticsProps {
  agentId?: string;
  agentIcon?: string;
  agentDisplayName?: string;
  /** Pass agents from useAgents so release timeline uses agents.json as single source of truth */
  agents?: Agent[];
}

// Agent color palette - matches agent card button colors
const AGENT_COLORS: Record<string, string> = {
  'aldo': '#2b2e32',              // Dark gray/black (main assistant)
  'hrpolicy': '#8b5cf6',          // Purple (PolicyTech - purple card)
  'orientation': '#ec4899',       // Pink (Orientation - pink card)
  'unifirst_docs': '#10b981',     // Green (UniFirst Docs - green card)
  'direct_embroidery': '#f97316', // Orange (Owensboro Production)
  'pwms-support': '#f97316',      // Orange (PWMS Support - orange card)
  'product': '#3b82f6',           // Blue (Product Searchlight - blue card)
  'iris': '#6f747b',              // Slate gray (IRIS)
  'owensboro2': '#ec4899',        // Pink (Owensboro)
  'service-desk': '#d946ef',      // Magenta (Service Desk)
  '__all__': '#059669'            // Emerald green (All Agents total)
};

const AGENT_NAMES: Record<string, string> = {
  'aldo': 'Scout',
  'hrpolicy': 'PolicyTech',
  'pwms-support': 'PWMS Support',
  'product': 'Product Searchlight',
  'orientation': 'Orientation',
  'unifirst_docs': 'UniFirst Docs',
  'direct_embroidery': 'Owensboro Production',
  'iris': 'IRIS',
  'owensboro2': 'Owensboro Production',
  'service-desk': 'Service Desk',
  '__all__': 'All Agents'
};

// Agent release dates for chart annotations
interface ReleaseNote {
  version: string;
  date: string;
  changes: string[];
}

interface AgentRelease {
  agentId: string;
  label: string;
  version: string;
  releaseNotes: ReleaseNote[];
}

interface ReleaseAnnotation {
  date: Date;
  agents: AgentRelease[];
}

// Hardcoded fallback annotations — used only when agents aren't supplied via props
const FALLBACK_RELEASE_ANNOTATIONS: ReleaseAnnotation[] = [
  {
    date: new Date('2025-12-17'),
    agents: [
      { agentId: 'aldo', label: 'Scout', version: '1.0', releaseNotes: [{ version: '1.0.0', date: 'Dec 17, 2025', changes: ['Initial release of Scout unified assistant', 'Multi-agent routing capability', 'Streaming responses'] }] },
      { agentId: 'orientation', label: 'Orientation', version: '1.0', releaseNotes: [{ version: '1.0.0', date: 'Dec 17, 2025', changes: ['Initial release', 'New employee onboarding guidance', 'Benefits and HR policy information'] }] },
      { agentId: 'hrpolicy', label: 'PolicyTech', version: '1.0', releaseNotes: [{ version: '1.0.0', date: 'Dec 17, 2025', changes: ['Initial release', 'HR policy document search', 'Policy question answering with citations'] }] }
    ]
  },
  {
    date: new Date('2026-01-13'),
    agents: [
      { agentId: 'unifirst_docs', label: 'UniFirst Docs', version: '1.0', releaseNotes: [{ version: '1.0.0', date: 'Jan 13, 2026', changes: ['Initial release', 'UniFirst documentation search', 'Technical document retrieval'] }] }
    ]
  },
  {
    date: new Date('2026-02-05'),
    agents: [
      { agentId: 'owensboro2', label: 'Owensboro Production', version: '1.0', releaseNotes: [{ version: '1.0.0', date: 'Feb 5, 2026', changes: ['Initial release', 'Owensboro Production support', 'Direct embroidery integration'] }] }
    ]
  }
];

/**
 * Build release annotations dynamically from agents.json data (via Agent[]).
 * Groups agents by releaseDate so the chart shows one marker per release date.
 * Falls back to FALLBACK_RELEASE_ANNOTATIONS when no agents are provided.
 */
function buildReleaseAnnotations(agents?: Agent[]): ReleaseAnnotation[] {
  if (!agents || agents.length === 0) return FALLBACK_RELEASE_ANNOTATIONS;

  // Collect agents that have a releaseDate (exclude hidden/disabled agents from timeline)
  const agentsWithRelease = agents.filter(a => a.releaseDate && !a.hidden);
  if (agentsWithRelease.length === 0) return FALLBACK_RELEASE_ANNOTATIONS;

  // Group by releaseDate
  const grouped: Record<string, AgentRelease[]> = {};
  for (const a of agentsWithRelease) {
    const dateKey = a.releaseDate!;
    if (!grouped[dateKey]) grouped[dateKey] = [];

    const releaseNotes: ReleaseNote[] = (a.versionHistory || [])
      .slice()
      .reverse()
      .map(entry => ({
        version: entry.version,
        date: entry.date,
        changes: entry.notes
          ? entry.notes.split(/[;,\n]/).map(s => s.trim()).filter(Boolean)
          : [`Version ${entry.version}`],
      }));

    // If no version history, create a single release note from the agent's current version
    if (releaseNotes.length === 0 && a.version) {
      releaseNotes.push({
        version: a.version,
        date: dateKey,
        changes: [`${a.name} version ${a.version} release`],
      });
    }

    grouped[dateKey].push({
      agentId: a.id,
      label: a.name,
      version: a.version || '1.0.0',
      releaseNotes,
    });
  }

  // Convert to sorted array of ReleaseAnnotation
  return Object.entries(grouped)
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([dateStr, agentReleases]) => ({
      date: new Date(dateStr),
      agents: agentReleases,
    }));
}

// Interface for keyword query results
interface KeywordQuery {
  timestamp: string;
  message: string;
  agentId: string;
  userId: string;
}

interface TopUser {
  email: string;
  currentCount: number;
  previousCount: number;
  pctChange: number | null;
  totalAllTime: number;
}

// Per-job-code usage row (from /analytics/app-insights/usage-by-jobcode)
interface JobCodeUsageRow {
  jobCode: string;
  title: string;
  users: number;
  activeUsers: number;
  queries: number;
  sessions: number;
}

interface JobCodeUsageTotals {
  users: number;
  activeUsers: number;
  queries: number;
  sessions: number;
  activeDaysThreshold: number;
}

interface PrevPeriodTotals {
  users: number;
  messages: number;
  sessions: number;
  newUsers?: number;
  multiDayUsers?: number;
  approvalRate?: number;
  allInteractions?: number;
}

const PercentChangeBadge: React.FC<{ current: number; previous: number; suffix?: string; mode?: 'relative' | 'points' }> = ({ current, previous, suffix = '', mode = 'relative' }) => {
  if (previous === 0 && current === 0) return null;
  if (mode === 'points') {
    const diff = Math.round(current - previous);
    if (diff === 0) {
      return <span className="ca-pct-badge ca-pct-flat" title="No change vs previous period"><Minus size={10} /> 0 pts</span>;
    }
    const isUp = diff > 0;
    return (
      <span className={`ca-pct-badge ${isUp ? 'ca-pct-up' : 'ca-pct-down'}`} title={`${isUp ? '+' : ''}${diff} pts vs previous period`}>
        {isUp ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
        {isUp ? '+' : ''}{diff} pts
      </span>
    );
  }
  if (previous === 0) {
    return <span className="ca-pct-badge ca-pct-new" title="New this period">new</span>;
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) {
    return <span className="ca-pct-badge ca-pct-flat" title="No change vs previous period"><Minus size={10} /> 0%{suffix}</span>;
  }
  const isUp = pct > 0;
  return (
    <span className={`ca-pct-badge ${isUp ? 'ca-pct-up' : 'ca-pct-down'}`} title={`${isUp ? '+' : ''}${pct}% vs previous period`}>
      {isUp ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
      {isUp ? '+' : ''}{pct}%{suffix}
    </span>
  );
};

const ComprehensiveAnalytics: React.FC<ComprehensiveAnalyticsProps> = ({ agentId, agentIcon, agentDisplayName, agents: propAgents }) => {
  // Build release annotations from agents.json data (single source of truth)
  const releaseAnnotations = useMemo(() => buildReleaseAnnotations(propAgents), [propAgents]);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [prevTotals, setPrevTotals] = useState<PrevPeriodTotals | null>(null);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('month');
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [focusedAgent, setFocusedAgent] = useState<string | null>(null);
  const [visibleAgents, setVisibleAgents] = useState<Set<string>>(new Set());
  const [isConfigured, setIsConfigured] = useState(true);
  const [presentationMode, setPresentationMode] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState<AgentRelease | null>(null);
  
  // State for clickable keyword feature
  const [expandedKeyword, setExpandedKeyword] = useState<string | null>(null);
  const [keywordQueries, setKeywordQueries] = useState<KeywordQuery[]>([]);
  const [keywordLoading, setKeywordLoading] = useState(false);
  
  // Timeline events toggle: 'all' shows all release annotations, 'selected' shows only for visible agents
  const [timelineEventFilter, setTimelineEventFilter] = useState<'all' | 'selected'>('all');

  // Job code filter — entries are { code, title } where title may be empty.
  // When title is non-empty, the dropdown renders "Title (Code)" for legibility.
  const [availableJobCodes, setAvailableJobCodes] = useState<Array<{ code: string; title: string }>>([]);
  const [selectedJobCode, setSelectedJobCode] = useState<string | null>(null);
  const [showJobCodeDropdown, setShowJobCodeDropdown] = useState(false);

  // Usage-by-job-code table + focused-agent totals. Scoped to the focused agent
  // (or the single-agent prop) so the table and KPI overlay update on focus.
  const [jobCodeUsage, setJobCodeUsage] = useState<JobCodeUsageRow[]>([]);
  const [jobCodeUsageTotals, setJobCodeUsageTotals] = useState<JobCodeUsageTotals | null>(null);
  const [jobCodeUsageLoading, setJobCodeUsageLoading] = useState(false);

  // Format a job code for display: "Branch Manager (148000)" when title known, else just the code.
  const formatJobCode = useCallback((code: string | null): string => {
    if (!code) return 'All Job Codes';
    const entry = availableJobCodes.find(jc => jc.code === code);
    return entry?.title ? `${entry.title} (${entry.code})` : code;
  }, [availableJobCodes]);

  const periodToDays: Record<TimePeriod, number | null> = {
    week: 7,
    month: 30,
    '3months': 90,
    '6months': 180,
    year: 365,
    all: null // All data since Dec 17, 2025
  };

  const periodLabels: Record<TimePeriod, string> = {
    week: 'This Week',
    month: 'This Month',
    '3months': 'Last 3 Months',
    '6months': 'Last 6 Months',
    year: 'This Year',
    all: 'All Time (Since Dec 17)'
  };

  // Fetch queries for a specific keyword
  const fetchKeywordQueries = useCallback(async (keyword: string) => {
    if (expandedKeyword === keyword) {
      // Collapse if already expanded
      setExpandedKeyword(null);
      setKeywordQueries([]);
      return;
    }

    try {
      setKeywordLoading(true);
      setExpandedKeyword(keyword);
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const days = periodToDays[timePeriod] || 30;
      
      // Include agent filter if viewing a specific agent's analytics
      // Use agentId prop OR focusedAgent for aggregate mode with agent focus
      const effectiveAgentId = agentId || focusedAgent;
      const agentParam = effectiveAgentId ? `&agentId=${encodeURIComponent(effectiveAgentId)}` : '';
      const url = `${apiUrl}/api/analytics/app-insights/keyword-queries?keyword=${encodeURIComponent(keyword)}&days=${days}&limit=50${agentParam}`;
      console.log('🔍 Fetching keyword queries:', { keyword, effectiveAgentId, url });
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch keyword queries');
      }
      
      const data = await response.json();
      setKeywordQueries(data.queries || []);
    } catch (err) {
      console.error('Error fetching keyword queries:', err);
      setKeywordQueries([]);
    } finally {
      setKeywordLoading(false);
    }
  }, [expandedKeyword, timePeriod, agentId, focusedAgent]);

  // Track if we're in single-agent mode
  const isSingleAgentMode = !!agentId;
  
  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        const days = periodToDays[timePeriod];
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        
        // Build URL with optional agentId and jobCode filters
        const params = new URLSearchParams();
        if (days) params.append('days', days.toString());
        if (agentId) params.append('agentId', agentId);
        if (selectedJobCode) params.append('jobCode', selectedJobCode);
        
        const queryString = params.toString();
        const url = `${apiUrl}/api/analytics/app-insights/adoption${queryString ? '?' + queryString : ''}`;
        
        // Build previous-period URL for period-over-period comparison (skip for "all time")
        let prevUrl: string | null = null;
        if (days) {
          const prevParams = new URLSearchParams();
          prevParams.append('days', days.toString());
          prevParams.append('offsetDays', days.toString());
          if (agentId) prevParams.append('agentId', agentId);
          if (selectedJobCode) prevParams.append('jobCode', selectedJobCode);
          prevUrl = `${apiUrl}/api/analytics/app-insights/adoption?${prevParams.toString()}`;
        }

        // Get auth token for authenticated requests (same approach as AgentDashboard)
        const authService = OidcAuthService.getInstance();
        await authService.ensureInitialized();
        
        // Prefer ID token (contains app roles), fall back to access token
        let token = await authService.getIdToken();
        if (!token) {
          token = await authService.getAccessToken();
        }
        
        const authHeaders: HeadersInit = {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        };
        
        // Build feedback URL - filter by agentId if in single-agent mode
        const feedbackParams = new URLSearchParams({ limit: '1' });
        if (agentId) {
          feedbackParams.append('agentId', agentId);
        }
        const feedbackUrl = `${apiUrl}/api/conversation-history/interactions?${feedbackParams.toString()}`;
        
        // Fetch analytics, previous period, and interaction stats in parallel
        const [analyticsResponse, interactionsResponse, prevResponse] = await Promise.all([
          fetch(url),
          fetch(feedbackUrl, { 
            headers: authHeaders
          }).catch((e) => {
            console.warn('Failed to fetch feedback stats:', e);
            return null;
          }),
          prevUrl ? fetch(prevUrl).catch(() => null) : Promise.resolve(null)
        ]);

        if (!analyticsResponse.ok) {
          throw new Error(`Failed to fetch analytics: ${analyticsResponse.statusText}`);
        }

        const data = await analyticsResponse.json();
        
        // Parse previous period data for comparison badges
        if (prevResponse && prevResponse.ok) {
          try {
            const prevData = await prevResponse.json();
            if (prevData.success && prevData.analytics?.totals) {
              const pt = prevData.analytics.totals;
              setPrevTotals({
                users: pt.users || 0,
                messages: pt.allInteractions || pt.messages || 0,
                sessions: pt.sessions || 0,
                newUsers: pt.newUsers,
                multiDayUsers: pt.multiDayUsers,
                approvalRate: prevData.analytics.feedback?.approvalRate,
                allInteractions: pt.allInteractions || pt.messages || 0,
              });
            }
          } catch { setPrevTotals(null); }
        } else {
          setPrevTotals(null);
        }
        
        // Parse feedback stats from interactions endpoint
        let feedbackStats: FeedbackStats | undefined;
        if (interactionsResponse) {
          if (interactionsResponse.ok) {
            try {
              const interactionsData = await interactionsResponse.json();
              console.log('📊 Feedback stats response:', interactionsData.stats);
              if (interactionsData.success && interactionsData.stats) {
                const { positive, negative, total, positiveRate } = interactionsData.stats;
                feedbackStats = {
                  positive: positive || 0,
                  negative: negative || 0,
                  total: total || 0,
                  approvalRate: positiveRate || (total > 0 ? Math.round((positive / total) * 100) : 0)
                };
              }
            } catch (e) {
              console.warn('Failed to parse feedback stats:', e);
            }
          } else {
            console.warn('Feedback request failed:', interactionsResponse.status, interactionsResponse.statusText);
          }
        }
        
        if (!data.configured) {
          setIsConfigured(false);
          setAnalytics({ ...data.analytics, feedback: feedbackStats });
        } else {
          setIsConfigured(true);
          setAnalytics({ ...data.analytics, feedback: feedbackStats });
          
          // Initialize visible agents (include __all__ synthetic agent)
          if (data.analytics?.agents) {
            setVisibleAgents(new Set(['__all__', ...data.analytics.agents.map((a: AgentData) => a.id)]));
          }
        }
      // Fetch top users (non-blocking, separate from main analytics)
      try {
        const topUsersParams = new URLSearchParams();
        if (days) topUsersParams.append('days', days.toString());
        if (agentId) topUsersParams.append('agentId', agentId);
        const topUsersUrl = `${apiUrl}/api/analytics/top-users?${topUsersParams.toString()}`;
        const topUsersResp = await fetch(topUsersUrl, { headers: authHeaders });
        if (topUsersResp.ok) {
          const topUsersData = await topUsersResp.json();
          if (topUsersData.success) setTopUsers(topUsersData.users || []);
        }
      } catch { setTopUsers([]); }

      } catch (err) {
        console.error('Failed to load analytics:', err);
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [timePeriod, agentId, selectedJobCode]);

  // Fetch available job codes (with titles) for filter dropdown.
  // Refetched when agentId changes so we get the most relevant titles
  // (AgentJobCodes table is scoped per agent).
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const params = new URLSearchParams({ days: '90' });
    if (agentId) params.append('agentId', agentId);
    fetch(`${apiUrl}/api/analytics/job-codes?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && Array.isArray(data.jobCodes) && data.jobCodes.length) {
          // Accept both new shape ({code, title}) and legacy shape (string[])
          const normalized = data.jobCodes.map((jc: unknown) =>
            typeof jc === 'string'
              ? { code: jc, title: '' }
              : { code: String((jc as { code: unknown }).code), title: String((jc as { title?: unknown }).title || '') }
          );
          // Sort: titled entries first (alphabetical by title), then bare codes (alphabetical)
          normalized.sort((a: { title: string; code: string }, b: { title: string; code: string }) => {
            if (a.title && !b.title) return -1;
            if (!a.title && b.title) return 1;
            if (a.title && b.title) return a.title.localeCompare(b.title);
            return a.code.localeCompare(b.code);
          });
          setAvailableJobCodes(normalized);
        }
      })
      .catch(() => {});
  }, [agentId]);

  // The agent that scopes the usage-by-job-code table and focused KPI overlay:
  // the single-agent prop wins; otherwise a focused agent in the main graph
  // (the synthetic "__all__" trace counts as no agent scope).
  const effectiveFocusAgent = useMemo(
    () => agentId || (focusedAgent && focusedAgent !== '__all__' ? focusedAgent : null),
    [agentId, focusedAgent]
  );

  // Fetch usage broken down by job code, scoped to the effective focus agent.
  // Refetched when the period or focused agent changes so the table + the
  // focused KPI stats (active users etc.) stay in sync with the main graph.
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const days = periodToDays[timePeriod] || 30;
    const params = new URLSearchParams({ days: String(days) });
    if (effectiveFocusAgent) params.append('agentId', effectiveFocusAgent);

    let cancelled = false;
    setJobCodeUsageLoading(true);
    fetch(`${apiUrl}/api/analytics/app-insights/usage-by-jobcode?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        if (data.success && Array.isArray(data.rows)) {
          setJobCodeUsage(data.rows);
          setJobCodeUsageTotals(data.totals || null);
        } else {
          setJobCodeUsage([]);
          setJobCodeUsageTotals(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setJobCodeUsage([]);
        setJobCodeUsageTotals(null);
      })
      .finally(() => { if (!cancelled) setJobCodeUsageLoading(false); });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timePeriod, effectiveFocusAgent]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.ca-time-period-selector') && !target.closest('.ca-jobcode-selector')) {
        setShowPeriodDropdown(false);
        setShowJobCodeDropdown(false);
      }
    };

    if (showPeriodDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showPeriodDropdown]);

  // Toggle agent visibility
  const toggleAgentVisibility = useCallback((agentId: string) => {
    setVisibleAgents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentId)) {
        newSet.delete(agentId);
      } else {
        newSet.add(agentId);
      }
      return newSet;
    });
    setFocusedAgent(null);
  }, []);

  // Focus on single agent
  const handleAgentFocus = useCallback((clickedAgentId: string) => {
    if (focusedAgent === clickedAgentId) {
      // Unfocus - show all agents
      setFocusedAgent(null);
      if (analytics?.agents) {
        setVisibleAgents(new Set(['__all__', ...analytics.agents.map(a => a.id)]));
      }
    } else {
      // Focus on this agent (keep __all__ visible if it was the clicked one)
      setFocusedAgent(clickedAgentId);
      if (clickedAgentId === '__all__') {
        setVisibleAgents(new Set(['__all__']));
      } else {
        setVisibleAgents(new Set([clickedAgentId]));
      }
    }
  }, [focusedAgent, analytics?.agents]);

  // Show all agents
  const showAllAgents = useCallback(() => {
    setFocusedAgent(null);
    if (analytics?.agents) {
      setVisibleAgents(new Set(['__all__', ...analytics.agents.map(a => a.id)]));
    }
  }, [analytics?.agents]);

  // Build the "All Agents" synthetic agent entry and enhanced agents list
  const allAgentEntry: AgentData | null = useMemo(() => {
    if (!analytics?.agents || analytics.agents.length === 0) return null;
    const totalMessages = analytics.agents.reduce((sum, a) => sum + a.totalMessages, 0);
    return {
      id: '__all__',
      name: 'All Agents',
      color: AGENT_COLORS['__all__'],
      totalMessages,
      totalUsers: analytics.totals.users,
      totalSessions: analytics.totals.sessions,
      dailyData: []
    };
  }, [analytics?.agents, analytics?.totals]);

  // Agents list including the synthetic "All Agents" entry (displayed in legend)
  const displayAgents = useMemo(() => {
    if (!analytics?.agents) return [];
    return allAgentEntry ? [allAgentEntry, ...analytics.agents] : analytics.agents;
  }, [analytics?.agents, allAgentEntry]);

  // When an agent is focused in the main graph (multi-agent mode), overlay the
  // agent-scoped usage totals onto the relevant KPI cards. Null when no agent
  // is focused (or in single-agent mode, where analytics.totals already scopes).
  const focusOverlay = useMemo<JobCodeUsageTotals | null>(() => {
    if (isSingleAgentMode) return null;
    if (!focusedAgent || focusedAgent === '__all__') return null;
    return jobCodeUsageTotals;
  }, [isSingleAgentMode, focusedAgent, jobCodeUsageTotals]);

  const focusOverlayAgentName = focusedAgent ? (AGENT_NAMES[focusedAgent] || focusedAgent) : '';

  // Calculate chart dimensions and data
  const chartData = useMemo(() => {
    if (!analytics?.timeSeries || analytics.timeSeries.length === 0) {
      return { points: [], maxValue: 0 };
    }

    const visibleTimeSeries = analytics.timeSeries.map(point => {
      const filteredPoint: TimeSeriesPoint = { date: point.date, total: 0 };
      let visibleTotal = 0;
      
      // Compute total across ALL agents for the __all__ line (regardless of visibility)
      let allAgentsTotal = 0;
      analytics.agents.forEach(agent => {
        const value = typeof point[agent.id] === 'number' ? point[agent.id] as number : 0;
        allAgentsTotal += value;
        if (visibleAgents.has(agent.id)) {
          filteredPoint[agent.id] = value;
          visibleTotal += value;
        }
      });

      // Always include __all__ value if that trace is visible
      if (visibleAgents.has('__all__')) {
        filteredPoint['__all__'] = allAgentsTotal;
      }
      
      filteredPoint.total = Math.max(visibleTotal, visibleAgents.has('__all__') ? allAgentsTotal : 0);
      return filteredPoint;
    });

    const maxValue = Math.max(...visibleTimeSeries.map(p => p.total), 1);

    return { points: visibleTimeSeries, maxValue };
  }, [analytics?.timeSeries, analytics?.agents, visibleAgents]);

  // Format number for display
  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  // Loading state
  if (loading) {
    return (
      <div className="ca-container">
        <div className="ca-loading">
          <div className="ca-loading-spinner"></div>
          <p>Loading analytics from Application Insights...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="ca-container">
        <div className="ca-error">
          <AlertCircle size={24} />
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // Not configured state
  if (!isConfigured) {
    return (
      <div className="ca-container">
        <div className="ca-not-configured">
          <Info size={32} />
          <h3>Application Insights Not Configured</h3>
          <p>
            To view real analytics data, configure Application Insights by setting:
          </p>
          <ul>
            <li><code>APPINSIGHTS_APPLICATION_ID</code></li>
            <li><code>APPINSIGHTS_API_KEY</code></li>
          </ul>
          <p className="ca-hint">
            These can be found in the Azure Portal under Application Insights → API Access
          </p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  return (
    <div className={`ca-container ${presentationMode ? 'ca-presentation-mode' : ''}`}>
      {/* Header */}
      <div className="ca-header">
        <div className="ca-header-left">
          <h2>
            <BarChart3 size={24} />
            {isSingleAgentMode 
              ? `${agentDisplayName || AGENT_NAMES[agentId!] || agentId} Analytics`
              : 'User Adoption Analytics'
            }
          </h2>
          <span className="ca-data-source">
            <Activity size={14} />
            Live from Azure Application Insights
          </span>
        </div>

        <div className="ca-header-right">
          <div className="ca-time-period-selector">
            <button 
              className="ca-period-button"
              onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
            >
              <Calendar size={16} />
              {periodLabels[timePeriod]}
              <ChevronDown className={`ca-chevron ${showPeriodDropdown ? 'open' : ''}`} size={16} />
            </button>
            {showPeriodDropdown && (
              <div className="ca-period-dropdown">
                {(Object.keys(periodLabels) as TimePeriod[]).map((period) => (
                  <button
                    key={period}
                    className={`ca-period-option ${period === timePeriod ? 'active' : ''}`}
                    onClick={() => {
                      setTimePeriod(period);
                      setShowPeriodDropdown(false);
                    }}
                  >
                    {periodLabels[period]}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Job Code Filter (only shown when job codes are available) */}
          {availableJobCodes.length > 0 && (
            <div className="ca-jobcode-selector">
              <button
                className={`ca-period-button ${selectedJobCode ? 'ca-jobcode-active' : ''}`}
                onClick={() => setShowJobCodeDropdown(!showJobCodeDropdown)}
                title={selectedJobCode ? `Filtering by job code ${selectedJobCode}` : 'Filter analytics by job code'}
              >
                <Briefcase size={16} />
                {formatJobCode(selectedJobCode)}
                <ChevronDown className={`ca-chevron ${showJobCodeDropdown ? 'open' : ''}`} size={16} />
              </button>
              {showJobCodeDropdown && (
                <div className="ca-period-dropdown">
                  <button
                    className={`ca-period-option ${!selectedJobCode ? 'active' : ''}`}
                    onClick={() => { setSelectedJobCode(null); setShowJobCodeDropdown(false); }}
                  >
                    All Job Codes
                  </button>
                  {availableJobCodes.map(jc => (
                    <button
                      key={jc.code}
                      className={`ca-period-option ${selectedJobCode === jc.code ? 'active' : ''}`}
                      onClick={() => { setSelectedJobCode(jc.code); setShowJobCodeDropdown(false); }}
                      title={jc.title ? `${jc.title} — ${jc.code}` : jc.code}
                    >
                      {jc.title ? `${jc.title} (${jc.code})` : jc.code}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Presentation Mode Toggle */}
          <button 
            className={`ca-presentation-btn ${presentationMode ? 'active' : ''}`}
            onClick={() => setPresentationMode(!presentationMode)}
            title={presentationMode ? 'Exit slideshow view' : 'Slideshow view'}
          >
            {presentationMode ? <X size={16} /> : <Presentation size={16} />}
            {presentationMode ? 'Exit' : 'Slides'}
          </button>
        </div>
      </div>

      {/* Executive KPI Cards - Different layout for single-agent vs multi-agent */}
      {isSingleAgentMode ? (
        /* Single Agent Mode: Show agent-specific KPIs */
        <div className="ca-kpi-grid ca-kpi-grid-5">
          {/* KPI 1: Agent Users */}
          <div className="ca-kpi-card">
            <div className="ca-kpi-icon">
              <Users size={24} />
            </div>
            <div className="ca-kpi-content">
              <span className="ca-kpi-value">
                {formatNumber(analytics.totals.users)}
                {prevTotals && <PercentChangeBadge current={analytics.totals.users} previous={prevTotals.users} />}
              </span>
              <span className="ca-kpi-label">Users</span>
              <span className="ca-kpi-breakdown">
                unique users of {agentDisplayName || AGENT_NAMES[agentId!] || agentId}
              </span>
            </div>
          </div>

          {/* KPI 2: Active Users */}
          <div className="ca-kpi-card">
            <div className="ca-kpi-icon">
              <RefreshCw size={24} />
            </div>
            <div className="ca-kpi-content">
              <span className="ca-kpi-value">
                {analytics.totals.users > 0 
                  ? Math.round(((analytics.totals.multiDayUsers || 0) / analytics.totals.users) * 100)
                  : 0}%
                {prevTotals && prevTotals.users > 0 && (
                  <PercentChangeBadge
                    current={analytics.totals.users > 0 ? Math.round(((analytics.totals.multiDayUsers || 0) / analytics.totals.users) * 100) : 0}
                    previous={Math.round(((prevTotals.multiDayUsers || 0) / prevTotals.users) * 100)}
                    mode="points"
                  />
                )}
              </span>
              <span className="ca-kpi-label">Active Users</span>
              <span className="ca-kpi-breakdown">
                {analytics.totals.multiDayUsers || 0} users active {analytics.totals.activeDaysThreshold || 2}+ days
              </span>
            </div>
          </div>

          {/* KPI 3: Agent Queries */}
          <div className="ca-kpi-card">
            <div className="ca-kpi-icon">
              <MessageCircle size={24} />
            </div>
            <div className="ca-kpi-content">
              <span className="ca-kpi-value">
                {formatNumber(analytics.totals.messages)}
                {prevTotals && <PercentChangeBadge current={analytics.totals.messages} previous={prevTotals.messages} />}
              </span>
              <span className="ca-kpi-label">Total Queries</span>
              <span className="ca-kpi-breakdown">
                {formatNumber(analytics.totals.sessions)} conversations
              </span>
            </div>
          </div>

          {/* KPI 4: Avg Queries per User */}
          <div className="ca-kpi-card">
            <div className="ca-kpi-icon">
              <Activity size={24} />
            </div>
            <div className="ca-kpi-content">
              <span className="ca-kpi-value">
                {analytics.totals.users > 0 
                  ? (analytics.totals.messages / analytics.totals.users).toFixed(1)
                  : '0'}
                {prevTotals && prevTotals.users > 0 && (
                  <PercentChangeBadge
                    current={analytics.totals.users > 0 ? analytics.totals.messages / analytics.totals.users : 0}
                    previous={prevTotals.messages / prevTotals.users}
                  />
                )}
              </span>
              <span className="ca-kpi-label">Queries per User</span>
              <span className="ca-kpi-breakdown">
                average engagement
              </span>
            </div>
          </div>

          {/* KPI 5: Approval Rate (Feedback) */}
          <div className="ca-kpi-card">
            <div className="ca-kpi-icon">
              <ThumbsUp size={24} />
            </div>
            <div className="ca-kpi-content">
              <span className="ca-kpi-value">
                {analytics.feedback ? `${analytics.feedback.approvalRate}%` : 'N/A'}
                {analytics.feedback && prevTotals?.approvalRate != null && (
                  <PercentChangeBadge current={analytics.feedback.approvalRate} previous={prevTotals.approvalRate} mode="points" />
                )}
              </span>
              <span className="ca-kpi-label">Approval Rate</span>
              <span className="ca-kpi-breakdown" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {analytics.feedback 
                  ? <>
                      {analytics.feedback.positive} <ThumbsUp size={12} className="text-green-500" style={{ display: 'inline' }} /> 
                      {' / '}
                      {analytics.feedback.negative} <ThumbsDown size={12} className="text-red-500" style={{ display: 'inline' }} />
                    </>
                  : 'No feedback data'}
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* Multi-Agent Mode: Full User Analytics Framework */
        <>
        {focusOverlay && (
          <div className="ca-focus-banner">
            <Target size={14} />
            <span>Usage stats scoped to <strong>{focusOverlayAgentName}</strong></span>
            <button className="ca-focus-banner-clear" onClick={showAllAgents}>
              <X size={12} /> Clear focus
            </button>
          </div>
        )}
        <div className="ca-kpi-grid ca-kpi-grid-5">
          {/* KPI 1: Total Users */}
          <div className="ca-kpi-card">
            <div className="ca-kpi-icon">
              <Users size={24} />
            </div>
            <div className="ca-kpi-content">
              <span className="ca-kpi-value">
                {formatNumber(focusOverlay ? focusOverlay.users : analytics.totals.users)}
                {!focusOverlay && prevTotals && <PercentChangeBadge current={analytics.totals.users} previous={prevTotals.users} />}
              </span>
              <span className="ca-kpi-label">Total Users</span>
              {focusOverlay ? (
                <span className="ca-kpi-breakdown">{focusOverlayAgentName} users</span>
              ) : analytics.totals.existingUsers !== undefined && (
                <span className="ca-kpi-breakdown">
                  {formatNumber(analytics.totals.newUsers || 0)} new • {formatNumber(analytics.totals.existingUsers)} existing
                </span>
              )}
            </div>
          </div>

          {/* KPI 2: New Users */}
          <div className="ca-kpi-card">
            <div className="ca-kpi-icon">
              <TrendingUp size={24} />
            </div>
            <div className="ca-kpi-content">
              <span className="ca-kpi-value">
                {formatNumber(analytics.totals.newUsers || 0)}
                {prevTotals && <PercentChangeBadge current={analytics.totals.newUsers || 0} previous={prevTotals.newUsers || 0} />}
              </span>
              <span className="ca-kpi-label">New Users</span>
              {analytics.totals.userSegments && (
                <span className="ca-kpi-breakdown">
                  {analytics.totals.userSegments.newOneTime} one-time • {analytics.totals.userSegments.newReturning} returned
                </span>
              )}
            </div>
          </div>

          {/* KPI 3: Active Users */}
          <div className="ca-kpi-card">
            <div className="ca-kpi-icon">
              <Activity size={24} />
            </div>
            <div className="ca-kpi-content">
              <span className="ca-kpi-value">
                {focusOverlay
                  ? (focusOverlay.users > 0 ? Math.round((focusOverlay.activeUsers / focusOverlay.users) * 100) : 0)
                  : (analytics.totals.users > 0
                      ? Math.round(((analytics.totals.multiDayUsers || 0) / analytics.totals.users) * 100)
                      : 0)}%
                {!focusOverlay && prevTotals && prevTotals.users > 0 && (
                  <PercentChangeBadge
                    current={analytics.totals.users > 0 ? Math.round(((analytics.totals.multiDayUsers || 0) / analytics.totals.users) * 100) : 0}
                    previous={Math.round(((prevTotals.multiDayUsers || 0) / prevTotals.users) * 100)}
                    mode="points"
                  />
                )}
              </span>
              <span className="ca-kpi-label">Active Users</span>
              <span className="ca-kpi-breakdown">
                {focusOverlay
                  ? `${focusOverlay.activeUsers} users active ${focusOverlay.activeDaysThreshold}+ days`
                  : `${analytics.totals.multiDayUsers || 0} users active ${analytics.totals.activeDaysThreshold || 2}+ days`}
              </span>
            </div>
          </div>

          {/* KPI 4: Total Queries */}
          <div className="ca-kpi-card">
            <div className="ca-kpi-icon">
              <MessageCircle size={24} />
            </div>
            <div className="ca-kpi-content">
              <span className="ca-kpi-value">
                {formatNumber(focusOverlay ? focusOverlay.queries : (analytics.totals.allInteractions || analytics.totals.messages))}
                {!focusOverlay && prevTotals && <PercentChangeBadge current={analytics.totals.allInteractions || analytics.totals.messages} previous={prevTotals.allInteractions || prevTotals.messages} />}
              </span>
              <span className="ca-kpi-label">Total Queries</span>
              <span className="ca-kpi-breakdown">
                {formatNumber(focusOverlay ? focusOverlay.sessions : analytics.totals.sessions)} conversations
              </span>
            </div>
          </div>

          {/* KPI 5: Approval Rate (Feedback) */}
          <div className="ca-kpi-card">
            <div className="ca-kpi-icon">
              <ThumbsUp size={24} />
            </div>
            <div className="ca-kpi-content">
              <span className="ca-kpi-value">
                {analytics.feedback ? `${analytics.feedback.approvalRate}%` : 'N/A'}
                {analytics.feedback && prevTotals?.approvalRate != null && (
                  <PercentChangeBadge current={analytics.feedback.approvalRate} previous={prevTotals.approvalRate} mode="points" />
                )}
              </span>
              <span className="ca-kpi-label">Approval Rate</span>
              <span className="ca-kpi-breakdown" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {analytics.feedback 
                  ? <>
                      {analytics.feedback.positive} <ThumbsUp size={12} className="text-green-500" style={{ display: 'inline' }} /> 
                      {' / '}
                      {analytics.feedback.negative} <ThumbsDown size={12} className="text-red-500" style={{ display: 'inline' }} />
                      {' of '}{analytics.feedback.total}
                    </>
                  : 'No feedback data'}
              </span>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Main Content - Chart & Topics Side by Side in Presentation Mode */}
      <div className="ca-main-content">
      {/* Chart Section */}
      <div className="ca-chart-section">
        <div className="ca-chart-header">
          <h3>
            <Layers size={18} />
            {isSingleAgentMode 
              ? `${AGENT_NAMES[agentId!] || agentId} Queries Over Time`
              : 'Queries per Agent Over Time'
            }
            <span className="ca-chart-subtitle" title={isSingleAgentMode 
              ? "Daily count of user queries handled by this agent"
              : "Daily count of user queries handled by each agent"
            }>
              <Info size={14} />
            </span>
          </h3>
          
          {!isSingleAgentMode && (
            <div className="ca-chart-controls">
              <div className="ca-timeline-toggle">
                <span className="ca-timeline-toggle-label">Releases:</span>
                <button 
                  className={`ca-timeline-btn ${timelineEventFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setTimelineEventFilter('all')}
                  title="Show all release timeline events"
                >
                  All
                </button>
                <button 
                  className={`ca-timeline-btn ${timelineEventFilter === 'selected' ? 'active' : ''}`}
                  onClick={() => setTimelineEventFilter('selected')}
                  title="Show releases only for selected agent(s)"
                >
                  Selected
                </button>
              </div>
              <button 
                className={`ca-show-all-btn ${!focusedAgent ? 'active' : ''}`}
                onClick={showAllAgents}
              >
                <Eye size={14} /> Show All
              </button>
            </div>
          )}
        </div>

        {/* Agent Legend/Toggle - Hide in single-agent mode */}
        {!isSingleAgentMode && (
          <div className="ca-agent-legend">
            {displayAgents.map(agent => (
              <button
                key={agent.id}
                className={`ca-agent-toggle ${visibleAgents.has(agent.id) ? 'visible' : 'hidden'} ${focusedAgent === agent.id ? 'focused' : ''} ${agent.id === '__all__' ? 'ca-all-agents-toggle' : ''}`}
                onClick={() => handleAgentFocus(agent.id)}
                style={{ 
                  '--agent-color': AGENT_COLORS[agent.id] || agent.color || '#6f747b' 
                } as React.CSSProperties}
              >
                <span className="ca-agent-dot"></span>
                <span className="ca-agent-name">{agent.name || AGENT_NAMES[agent.id] || agent.id}</span>
                <span className="ca-agent-count">{formatNumber(agent.totalMessages)}</span>
                {focusedAgent === agent.id && <Target size={12} className="ca-focus-icon" />}
              </button>
            ))}
          </div>
        )}

        {/* Line Chart */}
        <div className="ca-chart-container">
          {chartData.points.length > 0 ? (
            <svg className="ca-line-chart" viewBox="0 0 800 270" preserveAspectRatio="xMidYMid meet">
              {/* Grid lines */}
              <g className="ca-grid">
                {[0, 1, 2, 3, 4].map(i => (
                  <line 
                    key={i}
                    x1="50" 
                    y1={40 + i * 45} 
                    x2="780" 
                    y2={40 + i * 45}
                    stroke="#e3e5e8"
                    strokeDasharray="4 4"
                  />
                ))}
              </g>

              {/* Y-axis labels */}
              <g className="ca-y-labels">
                {[0, 1, 2, 3, 4].map(i => (
                  <text 
                    key={i}
                    x="45" 
                    y={45 + i * 45}
                    textAnchor="end"
                    fill="#6f747b"
                    fontSize="10"
                  >
                    {formatNumber(Math.round(chartData.maxValue * (4 - i) / 4))}
                  </text>
                ))}
              </g>

              {/* Lines for each agent (including All Agents synthetic line) */}
              {displayAgents.filter(a => visibleAgents.has(a.id)).map(agent => {
                const points = chartData.points.map((point, idx) => {
                  const x = 50 + (idx / (chartData.points.length - 1 || 1)) * 730;
                  const value = typeof point[agent.id] === 'number' ? point[agent.id] as number : 0;
                  const y = 220 - (value / chartData.maxValue) * 180;
                  return `${x},${y}`;
                }).join(' ');

                const isAllAgents = agent.id === '__all__';
                return (
                  <g key={agent.id}>
                    <polyline
                      fill="none"
                      stroke={AGENT_COLORS[agent.id] || agent.color || '#6f747b'}
                      strokeWidth={isAllAgents ? 2.5 : (focusedAgent === agent.id ? 3 : 2)}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray={isAllAgents ? '6 3' : undefined}
                      points={points}
                      opacity={focusedAgent && focusedAgent !== agent.id ? 0.3 : (isAllAgents ? 0.7 : 1)}
                    />
                    {/* Data points (skip dots for All Agents line to keep it clean) */}
                    {!isAllAgents && chartData.points.length <= 31 && chartData.points.map((point, idx) => {
                      const x = 50 + (idx / (chartData.points.length - 1 || 1)) * 730;
                      const value = typeof point[agent.id] === 'number' ? point[agent.id] as number : 0;
                      const y = 220 - (value / chartData.maxValue) * 180;
                      return (
                        <circle
                          key={idx}
                          cx={x}
                          cy={y}
                          r={focusedAgent === agent.id ? 4 : 3}
                          fill={AGENT_COLORS[agent.id] || agent.color || '#6f747b'}
                          opacity={focusedAgent && focusedAgent !== agent.id ? 0.3 : 1}
                        />
                      );
                    })}
                  </g>
                );
              })}

              {/* X-axis labels */}
              <g className="ca-x-labels">
                {chartData.points.filter((_, i) => 
                  chartData.points.length <= 14 || i % Math.ceil(chartData.points.length / 10) === 0
                ).map((point, idx, filtered) => {
                  const originalIdx = chartData.points.indexOf(point);
                  const x = 50 + (originalIdx / (chartData.points.length - 1 || 1)) * 730;
                  const date = new Date(point.date);
                  const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  return (
                    <text 
                      key={idx}
                      x={x} 
                      y={250}
                      textAnchor="middle"
                      fill="#6f747b"
                      fontSize="10"
                    >
                      {label}
                    </text>
                  );
                })}
              </g>

              {/* Release Date Annotations */}
              <g className="ca-release-annotations">
                {releaseAnnotations.map((annotation, annotationIdx) => {
                  // Calculate chart date bounds
                  const chartStartDate = chartData.points.length > 0 
                    ? new Date(chartData.points[0].date)
                    : null;
                  const chartEndDate = chartData.points.length > 0 
                    ? new Date(chartData.points[chartData.points.length - 1].date)
                    : null;
                  
                  // Only show annotation if it falls within the chart's date range
                  if (!chartStartDate || !chartEndDate) return null;
                  if (annotation.date < chartStartDate || annotation.date > chartEndDate) return null;

                  // Filter agents in this annotation based on timeline toggle
                  const filteredAnnotationAgents = timelineEventFilter === 'selected'
                    ? annotation.agents.filter(a => visibleAgents.has(a.agentId))
                    : annotation.agents;

                  // Skip this annotation entirely if no agents match the filter
                  if (filteredAnnotationAgents.length === 0) return null;
                  
                  // Calculate X position for the vertical line
                  const totalDays = (chartEndDate.getTime() - chartStartDate.getTime()) / (1000 * 60 * 60 * 24);
                  const daysFromStart = (annotation.date.getTime() - chartStartDate.getTime()) / (1000 * 60 * 60 * 24);
                  const lineX = 50 + (daysFromStart / totalDays) * 730;
                  
                  // Bubble dimensions
                  const bubbleHeight = 18;
                  const bubbleGap = 3;
                  const bubblePadding = 12;
                  
                  return (
                    <g key={annotationIdx} className="ca-release-marker">
                      {/* Vertical line - use first agent's color */}
                      <line
                        x1={lineX}
                        y1={8 + filteredAnnotationAgents.length * (bubbleHeight + bubbleGap)}
                        x2={lineX}
                        y2={230}
                        stroke={AGENT_COLORS[filteredAnnotationAgents[0].agentId] || '#6f747b'}
                        strokeWidth={2}
                        strokeDasharray="6 4"
                        opacity={0.6}
                      />
                      
                      {/* Stacked agent bubbles */}
                      {filteredAnnotationAgents.map((agent, agentIdx) => {
                        const agentColor = AGENT_COLORS[agent.agentId] || '#6f747b';
                        const labelText = `${agent.label} v${agent.version}`;
                        const labelWidth = Math.max(85, labelText.length * 5.5 + bubblePadding);
                        const labelHalfWidth = labelWidth / 2;
                        
                        // Clamp label position to stay within chart bounds
                        const minLabelX = 50 + labelHalfWidth;
                        const maxLabelX = 780 - labelHalfWidth;
                        const labelX = Math.max(minLabelX, Math.min(maxLabelX, lineX));
                        
                        const bubbleY = 5 + agentIdx * (bubbleHeight + bubbleGap);
                        
                        return (
                          <g 
                            key={agentIdx} 
                            className="ca-release-bubble"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedRelease(agent)}
                          >
                            {/* Bubble background */}
                            <rect
                              x={labelX - labelHalfWidth}
                              y={bubbleY}
                              width={labelWidth}
                              height={bubbleHeight}
                              rx={9}
                              fill={agentColor}
                              opacity={0.95}
                              className="ca-bubble-bg"
                            />
                            {/* Bubble text */}
                            <text
                              x={labelX}
                              y={bubbleY + 13}
                              textAnchor="middle"
                              fill="white"
                              fontSize="9"
                              fontWeight="600"
                              style={{ pointerEvents: 'none' }}
                            >
                              {labelText}
                            </text>
                          </g>
                        );
                      })}
                    </g>
                  );
                })}
              </g>
            </svg>
          ) : (
            <div className="ca-no-chart-data">
              <BarChart3 size={48} />
              <p>No data available for the selected time period</p>
            </div>
          )}
        </div>
      </div>

      {/* Trending Keywords Section */}
      <div className="ca-topics-section">
        <div className="ca-topics-grid ca-topics-grid-single">
          <div className="ca-topics-card">
            <h4>
              Trending Keywords
              {(focusedAgent || isSingleAgentMode) && (
                <span className="ca-keyword-filter">
                  for {isSingleAgentMode 
                    ? (agentDisplayName || AGENT_NAMES[agentId!] || agentId)
                    : (AGENT_NAMES[focusedAgent!] || focusedAgent)
                  }
                </span>
              )}
            </h4>
            {(() => {
              // Get keywords based on focused agent, single agent mode, or show all
              const keywordsByAgent = analytics.commonTopics.keywordsByAgent;
              let displayKeywords = analytics.commonTopics.keywords;
              
              // For single-agent mode, use the agentId
              const effectiveAgent = isSingleAgentMode ? agentId : focusedAgent;
              
              if (effectiveAgent && keywordsByAgent && keywordsByAgent[effectiveAgent]) {
                displayKeywords = keywordsByAgent[effectiveAgent];
              }
              
              if (displayKeywords.length > 0) {
                const maxCount = Math.max(...displayKeywords.map(k => k.count));
                return (
                  <>
                    <div className="ca-keyword-cloud">
                      {displayKeywords.slice(0, 15).map((kw, idx) => (
                        <button 
                          key={idx}
                          className={`ca-keyword ca-keyword-clickable ${expandedKeyword === kw.word ? 'ca-keyword-active' : ''}`}
                          onClick={() => fetchKeywordQueries(kw.word)}
                          style={{
                            fontSize: `${Math.min(1.2, 0.75 + (kw.count / maxCount) * 0.5)}rem`,
                            opacity: expandedKeyword === kw.word ? 1 : (0.6 + (kw.count / maxCount) * 0.4)
                          }}
                        >
                          {kw.word}
                          <span className="ca-keyword-count">{kw.count}</span>
                          <ChevronDown 
                            size={12} 
                            className={`ca-keyword-chevron ${expandedKeyword === kw.word ? 'ca-keyword-chevron-open' : ''}`}
                          />
                        </button>
                      ))}
                    </div>
                    
                    {/* Keyword Queries Dropdown */}
                    {expandedKeyword && (
                      <div className="ca-keyword-dropdown">
                        <div className="ca-keyword-dropdown-header">
                          <span>Queries containing "<strong>{expandedKeyword}</strong>"</span>
                          <button 
                            className="ca-keyword-dropdown-close"
                            onClick={() => { setExpandedKeyword(null); setKeywordQueries([]); }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                        
                        {keywordLoading ? (
                          <div className="ca-keyword-dropdown-loading">
                            <RefreshCw size={14} className="ca-spin" />
                            Loading queries...
                          </div>
                        ) : keywordQueries.length > 0 ? (
                          <div className="ca-keyword-dropdown-list">
                            {keywordQueries.map((query, idx) => (
                              <div key={idx} className="ca-keyword-query-item">
                                <div className="ca-keyword-query-meta">
                                  <span 
                                    className="ca-keyword-query-agent"
                                    style={{ 
                                      backgroundColor: AGENT_COLORS[query.agentId] || '#6f747b',
                                      color: '#fff'
                                    }}
                                  >
                                    {AGENT_NAMES[query.agentId] || query.agentId}
                                  </span>
                                  <span className="ca-keyword-query-time">
                                    {new Date(query.timestamp).toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="ca-keyword-query-text">{query.message}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="ca-keyword-dropdown-empty">
                            No queries found for this keyword
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              }
              return <p className="ca-no-data">No keyword data available</p>;
            })()}
          </div>
        </div>
      </div>
      </div>{/* End ca-main-content */}

      {/* Usage by Job Code — updates when an agent is focused in the graph;
          click a row to filter the rest of the dashboard by that job code. */}
      {(jobCodeUsageLoading || jobCodeUsage.length > 0) && (
        <div className="ca-jobcode-usage-section">
          <h3>
            <Briefcase size={18} />
            Usage by Job Code
            {effectiveFocusAgent && (
              <span className="ca-keyword-filter">
                for {agentId
                  ? (agentDisplayName || AGENT_NAMES[agentId] || agentId)
                  : focusOverlayAgentName}
              </span>
            )}
            <span className="ca-chart-subtitle" title="Interactions grouped by the user's job code. Click a row to filter the dashboard by that job code.">
              <Info size={14} />
            </span>
          </h3>
          {jobCodeUsageLoading ? (
            <div className="ca-keyword-dropdown-loading">
              <RefreshCw size={14} className="ca-spin" /> Loading usage by job code...
            </div>
          ) : (
            <div className="ca-table-container">
              <table className="ca-power-users-table ca-jobcode-usage-table">
                <thead>
                  <tr>
                    <th>Job Code</th>
                    <th style={{ textAlign: 'right' }}>Users</th>
                    <th style={{ textAlign: 'right' }}>Active</th>
                    <th style={{ textAlign: 'right' }}>Queries</th>
                    <th style={{ textAlign: 'right' }}>Conversations</th>
                  </tr>
                </thead>
                <tbody>
                  {jobCodeUsage.map(row => {
                    const isSelected = selectedJobCode === row.jobCode;
                    return (
                      <tr
                        key={row.jobCode}
                        className={`ca-jobcode-usage-row ${isSelected ? 'focused' : ''}`}
                        onClick={() => setSelectedJobCode(isSelected ? null : row.jobCode)}
                        title={isSelected ? 'Click to clear job code filter' : 'Click to filter dashboard by this job code'}
                      >
                        <td>
                          <span className="ca-jobcode-usage-name">
                            {row.title || row.jobCode}
                          </span>
                          {row.title && <span className="ca-jobcode-usage-code">{row.jobCode}</span>}
                          {isSelected && <Target size={12} className="ca-focus-icon" />}
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(row.users)}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(row.activeUsers)}</td>
                        <td style={{ textAlign: 'right' }}><strong>{formatNumber(row.queries)}</strong></td>
                        <td style={{ textAlign: 'right' }}>{formatNumber(row.sessions)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Agent Breakdown Table - HIDDEN: user/session attribution needs ChatResponse events to work properly */}
      {false && !isSingleAgentMode && (
        <div className="ca-breakdown-section">
          <h3>
            <BarChart3 size={18} />
            Agent Performance Breakdown
          </h3>
          
          <div className="ca-table-container">
            <table className="ca-breakdown-table">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Messages</th>
                  <th>Users</th>
                  <th>Sessions</th>
                  <th>Avg/Day</th>
                  <th>Share</th>
                </tr>
              </thead>
              <tbody>
                {analytics?.agents
                  ?.sort((a, b) => b.totalMessages - a.totalMessages)
                  .map(agent => {
                    const avgPerDay = (analytics?.totals?.daysTracked ?? 0) > 0 
                      ? Math.round(agent.totalMessages / (analytics?.totals?.daysTracked ?? 1) * 10) / 10
                      : 0;
                    const share = (analytics?.totals?.messages ?? 0) > 0
                      ? Math.round(agent.totalMessages / (analytics?.totals?.messages ?? 1) * 100)
                      : 0;
                    
                    return (
                      <tr 
                        key={agent.id}
                        className={focusedAgent === agent.id ? 'focused' : ''}
                        onClick={() => handleAgentFocus(agent.id)}
                      >
                        <td>
                          <span 
                            className="ca-agent-indicator"
                            style={{ backgroundColor: AGENT_COLORS[agent.id] || agent.color }}
                          />
                          {agent.name || AGENT_NAMES[agent.id] || agent.id}
                        </td>
                        <td><strong>{formatNumber(agent.totalMessages)}</strong></td>
                        <td>{formatNumber(agent.totalUsers)}</td>
                        <td>{formatNumber(agent.totalSessions)}</td>
                        <td>{avgPerDay}</td>
                        <td>
                          <div className="ca-share-bar">
                            <div 
                              className="ca-share-fill"
                              style={{ 
                                width: `${share}%`,
                                backgroundColor: AGENT_COLORS[agent.id] || agent.color
                              }}
                            />
                            <span>{share}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Users Section */}
      {topUsers.length > 0 && (
        <div className="ca-power-users-section">
          <h3>
            <Trophy size={18} />
            Top Users {isSingleAgentMode ? `— ${agentDisplayName || AGENT_NAMES[agentId!] || agentId}` : ''}
          </h3>
          <table className="ca-power-users-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Email</th>
                <th>Interactions</th>
                <th>vs Prev Period</th>
                <th style={{ textAlign: 'right' }}>All Time</th>
              </tr>
            </thead>
            <tbody>
              {topUsers.map((u, i) => (
                <tr key={u.email}>
                  <td className="ca-power-users-rank">{i + 1}</td>
                  <td>{u.email}</td>
                  <td>{u.currentCount}</td>
                  <td>
                    {u.pctChange != null ? (
                      <PercentChangeBadge current={u.currentCount} previous={u.previousCount} />
                    ) : (
                      <span className="ca-pct-badge ca-pct-new">new</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{u.totalAllTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      <div className="ca-footer">
        <p>
          <Info size={14} />
          Data since {analytics.dateRange.start || 'Dec 17, 2025'} • 
          Pre-made quick questions excluded from analysis • 
          HR routing de-duplicated with Scout
        </p>
      </div>

      {/* Release Notes Modal */}
      {selectedRelease && (
        <div className="ca-modal-overlay" onClick={() => setSelectedRelease(null)}>
          <div className="ca-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ca-modal-header" style={{ borderLeftColor: AGENT_COLORS[selectedRelease.agentId] || '#6f747b' }}>
              <div className="ca-modal-title">
                <span 
                  className="ca-modal-agent-dot"
                  style={{ backgroundColor: AGENT_COLORS[selectedRelease.agentId] || '#6f747b' }}
                />
                <h3>{selectedRelease.label} v{selectedRelease.version}</h3>
              </div>
              <button className="ca-modal-close" onClick={() => setSelectedRelease(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="ca-modal-content">
              <h4>Release Notes</h4>
              {selectedRelease.releaseNotes.map((note, idx) => (
                <div key={idx} className="ca-release-note">
                  <div className="ca-release-note-header">
                    <span className="ca-release-version">v{note.version}</span>
                    <span className="ca-release-date">{note.date}</span>
                  </div>
                  <ul className="ca-release-changes">
                    {note.changes.map((change, changeIdx) => (
                      <li key={changeIdx}>{change}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComprehensiveAnalytics;

